// src/components/hooks/useRecentLeads.ts
import { useMemo } from "react";
import type { Lead } from "@/types/lead";
import {
  applyLeadsManagerListFilters,
  type LeadsManagerListFilters,
} from "@/lib/lead/leadsManagerFilters";

export type RecentFilter = "all" | "today" | "unsent";
export type RecentSort = "recent" | "oldest" | "status";

export type UseRecentLeadsArgs = {
  leads: Lead[];
  showAllRecentLeads: boolean;
  recentFilter: RecentFilter;
  recentSort: RecentSort;
  managerListFilters?: LeadsManagerListFilters;
};

export type UseRecentLeadsResult = {
  recentLeads: Lead[];
  filteredRecentLeads: Lead[];
  visibleRecentLeads: Lead[];
  hasMoreLeads: boolean;

  todaysLeads: Lead[];
  todaysSent: number;
  todaysUnsent: number;
  /** All unsent non-test leads across all time — used for urgency messaging. */
  totalUnsent: number;
  queuedCount: number;

  isToday: (ts: number) => boolean;
};

export function useRecentLeads({
  leads,
  showAllRecentLeads,
  recentFilter,
  recentSort,
  managerListFilters,
}: UseRecentLeadsArgs): UseRecentLeadsResult {
  return useMemo(() => {
    const recentLeads = [...(leads || [])].sort((a, b) => b.lastUpdated - a.lastUpdated);

    const todayStr = new Date().toDateString();
    const isToday = (ts: number) => new Date(ts).toDateString() === todayStr;

    const todaysLeads = recentLeads.filter((l) => isToday(l.lastUpdated) && !l.isTest);
    const todaysSent = todaysLeads.filter((l) => l.sent).length;
    const todaysUnsent = todaysLeads.filter((l) => !l.sent).length;
    const totalUnsent = recentLeads.filter((l) => !l.sent && !l.isTest).length;

    const queuedCount = recentLeads.filter((l) => l.queued && !l.sent && !l.isTest).length;

    let filteredRecentLeads = recentLeads;

    if (recentFilter === "today") {
      filteredRecentLeads = recentLeads.filter((l) => isToday(l.lastUpdated) && !l.isTest);
    } else if (recentFilter === "unsent") {
      filteredRecentLeads = recentLeads.filter((l) => !l.sent && !l.isTest);
    }

    filteredRecentLeads = applyLeadsManagerListFilters(filteredRecentLeads, managerListFilters);

    if (recentSort === "recent") {
      filteredRecentLeads = [...filteredRecentLeads].sort((a, b) => b.lastUpdated - a.lastUpdated);
    } else if (recentSort === "oldest") {
      filteredRecentLeads = [...filteredRecentLeads].sort((a, b) => a.lastUpdated - b.lastUpdated);
    } else if (recentSort === "status") {
      const statusRank = (l: Lead) => {
        if (l.isTest) return 3;
        if (l.sent) return 2;
        if (l.queued) return 0;
        return 1; // draft
      };

      filteredRecentLeads = [...filteredRecentLeads].sort((a, b) => {
        const sa = statusRank(a);
        const sb = statusRank(b);
        if (sa !== sb) return sa - sb;
        return b.lastUpdated - a.lastUpdated;
      });
    }

    const visibleRecentLeads = showAllRecentLeads ? filteredRecentLeads : filteredRecentLeads.slice(0, 3);
    const hasMoreLeads = filteredRecentLeads.length > visibleRecentLeads.length;

    return {
      recentLeads,
      filteredRecentLeads,
      visibleRecentLeads,
      hasMoreLeads,

      todaysLeads,
      todaysSent,
      todaysUnsent,
      totalUnsent,
      queuedCount,

      isToday,
    };
  }, [leads, showAllRecentLeads, recentFilter, recentSort, managerListFilters]);
}